<app-dialog [formGroup]="paymentPlanForm" [size]="DialogSize.SMALL">
  <!-- Dialog Header -->
  <app-dialog-header>Set up recurring payment</app-dialog-header>

  <!-- Request Error -->
  <div *ngIf="didRequestError$ | async" class="alert alert--error mb-6">
    Attempt to save has failed, please try your request again later.
  </div>

  <!-- Plan Change Notification Warning -->
  <div *ngIf="authAdaptor.isAdmin" class="alert">
    {{
      data.isEditPaymentPlan && !paymentMethodIsFromUser
        ? 'The person who created this recurring payment (' + data.paymentPlanUi?.contact?.contactInfo?.fullName + ') will receive a notification of any changes made.'
        : 'Any client contacts configured to receive payment notifications will receive a notification of any changes made.'
    }}
  </div>

  <!-- Recurring Payment Name -->
  <label class="w-fit-content body-400">Recurring Payment Name</label>
  <input
    [formControlName]="PaymentPlanFormKey.NAME"
    type="text"
    class="form-input mt-2 w-100"
    [ngClass]="{ 'form-input--invalid': controlHasError(PaymentPlanFormKey.NAME) }"
  >
  <mat-error *ngIf="controlHasError(PaymentPlanFormKey.NAME)" class="small-300 color-red-500 mt-2">
    A name for the recurring payment is required.
  </mat-error>

  <!-- Client selection -->
  <label class="mt-6 w-fit-content body-400">Account</label>
  <auto-complete
    [formControlName]="PaymentPlanFormKey.CLIENT"
    [options]="clientOptions"
    [compareToFn]="compareClientsFn"
    [triggerDisplayFn]="clientsTriggerDisplayFn"
    [sortFn]="clientsSortFn"
    [disableOptionFn]="clientsDisableOptionFn"
    [customPanelWidth]="panelWidth$ | async"
    class="d-block w-100 mt-2"
  ></auto-complete>
  <mat-error *ngIf="controlHasError(PaymentPlanFormKey.CLIENT)" class="small-300 color-red-500 mt-2">
    An account for the recurring payment is required.
  </mat-error>

  <div class="mt-6 d-grid payment-detail-inputs-grid">
    <div>
      <label class="w-fit-content body-400">Amount</label>
      <input
        [formControlName]="PaymentPlanFormKey.AMOUNT"
        type="text"
        class="form-input form-input--amount mt-2 w-100"
        numberOnly
        currencyMask
        [options]="{ prefix: '', align: 'left', allowNegative: 'false' }"
        [ngClass]="{ 'form-input--invalid': controlHasError(PaymentPlanFormKey.AMOUNT) }"
      >
      <mat-error *ngIf="controlHasError(PaymentPlanFormKey.AMOUNT, 'required')" class="small-300 color-red-500 mt-2">
        An amount for the recurring payment is required.
      </mat-error>
    </div>

    <div>
      <label class="w-fit-content body-400">Frequency</label>
      <select
        [formControlName]="PaymentPlanFormKey.FREQUENCY"
        class="form-input form-select mt-2 w-100"
        [ngClass]="{ 'form-input--invalid': controlHasError(PaymentPlanFormKey.FREQUENCY) }"
      >
        <option *ngFor="let frequency of paymentPlanService.frequencyOptions" [value]="frequency">
          {{ frequencyDisplayMap[frequency] }}
        </option>
      </select>
      <mat-error *ngIf="controlHasError(PaymentPlanFormKey.FREQUENCY)" class="small-300 color-red-500 mt-2">
        A frequency for the recurring payment is required.
      </mat-error>
    </div>

    <div>
      <label class="w-fit-content body-400">Start Date</label>
      <input
        [formControlName]="PaymentPlanFormKey.START_DATE"
        [dpDayPicker]="datePickerConfigStartDate"
        placeholder="Select..."
        class="form-input form-input--datepicker mt-2 w-100"
        [ngClass]="{ 'form-input--invalid': controlHasError(PaymentPlanFormKey.START_DATE) }"
      >
      <ng-container *ngIf="controlHasError(PaymentPlanFormKey.START_DATE)">
        <mat-error *ngIf="controlHasError(PaymentPlanFormKey.START_DATE, 'required')" class="small-300 color-red-500 mt-2">
          A start date for the recurring payment is required
        </mat-error>

        <mat-error *ngIf="controlHasError(PaymentPlanFormKey.START_DATE, 'invalidStartDate')" class="small-300 color-red-500 mt-2">
          Start Date must be set to today or later
        </mat-error>

        <mat-error *ngIf="controlHasError(PaymentPlanFormKey.START_DATE, 'startAfterToday')" class="small-300 color-red-500 mt-2">
          Start Date must be set to tomorrow or later
        </mat-error>
      </ng-container>
    </div>

    <div>
      <label class="w-fit-content body-400">End Date</label>
      <input
        [formControlName]="PaymentPlanFormKey.END_DATE"
        [dpDayPicker]="datePickerConfigEndDate"
        placeholder="Select..."
        class="form-input form-input--datepicker mt-2 w-100"
        [ngClass]="{ 'form-input--invalid': controlHasError(PaymentPlanFormKey.END_DATE) }"
      >
      <mat-error *ngIf="controlHasError(PaymentPlanFormKey.END_DATE, 'required')" class="small-300 color-red-500 mt-2">
        An end date for the recurring payment is required.
      </mat-error>

    </div>
  </div>

  <mat-error *ngIf="controlHasError(PaymentPlanFormKey.END_DATE, 'invalidEndDate')" class="small-300 color-red-500 mt-2">
    <!-- TODO REFACTOR: Should we replace "a later date than" to "after"? -->
    End Date must be set to a later date than the start date
  </mat-error>

  <!-- Select Payment Method -->
  <div *ngIf="paymentMethodIsFromUser && (savedPaymentMethodsExist$ | async) === false" class="mt-2 tc--warning">
    A saved payment method is required to set up a recurring payment. Please create one now.
  </div>
  <app-select-payment-method
    [intent]="PaymentMethodIntent.PAYMENT"
    [paymentFlow]="PaymentFlow.PLAN"
    [paymentMethodMode]="paymentMethodMode$ | async"
    [selectedPaymentMethodId]="selectedPaymentMethodId$ | async"
    [workflow]="PaymentsWorkflowType.PAYMENT_PLAN"
    [surface]="'settings-payment-plan-modal'"
    (paymentMethodModeChange)="paymentMethodMode$.next($event)"
    (paymentMethodSelected)="onSavedPaymentMethodSelected($event)"
    (click)="!paymentMethodIsFromUser && onSelectPaymentMethodComponentClicked($event)"
    class="mt-6"
  ></app-select-payment-method>

  <!-- Payment Summary -->
  <app-payment-summary
    [overriddenSelectedPaymentMethod$]="selectedPaymentMethod$"
    [overriddenSubtotal$]="recurringPaymentAmount$"
    [paymentFlow]="PaymentFlow.PLAN"
    [ignoreLateFees]="true"
    [currency]="selectedClientCurrency$ | async"
    class="mt-6"
  ></app-payment-summary>

  <!-- Specify Email Addresses for Payment Receipts -->
  <app-recipient-emails
    [disabled]="!paymentPlanForm.value"
    [primaryEmail]="primaryEmail"
    [additionalEmails]="additionalEmails"
    class="mt-6"
    (additionalEmailsChanged)="onAdditionalEmailsChanged($event)"
  ></app-recipient-emails>

  <!-- Dialog Footer -->
  <app-dialog-footer>
    <button andButton color="primary" type="submit" [isLoading]="isProcessingRequest$ | async" (click)="savePaymentPlan()">
      Save
    </button>
  </app-dialog-footer>
</app-dialog>
